import type { WallpaperFit } from '../types/content';
import type { WindowInstance } from '../types/window';
import { isFit } from '../os/wallpaper';
import { loadPref, savePref } from '../os/prefs';

export type View = 'room' | 'desktop';
export type Transition = 'none' | 'zoom-in' | 'zoom-out';

export interface AppState {
  view: View;
  transition: Transition;
  windows: WindowInstance[];
  focusedId: string | null;
  startOpen: boolean;
  userScale: number;
  userPan: { x: number; y: number };
  audioUnlocked: boolean;
  ambientOn: boolean;
  musicOn: boolean;
  reducedMotion: boolean;
  bootSplash: boolean;
  queuedHash: string | null;
  lastEgg: string | null;
  mute: boolean;
  wallpaperId: string;
  wallpaperFit: WallpaperFit;
  desktopColor: string;
  uiSounds: boolean;
  skipBoot: boolean;
}

export type Event =
  | { type: 'ENTER_DESKTOP'; via: 'crt' | 'hash' | 'keyboard' }
  | { type: 'LEAVE_DESKTOP'; via: 'shutdown' | 'hash' }
  | { type: 'TRANSITION_END' }
  | { type: 'OPEN_WINDOW'; appId: string; payload?: Record<string, unknown> }
  | { type: 'CLOSE_WINDOW'; id: string }
  | { type: 'FOCUS_WINDOW'; id: string }
  | { type: 'MOVE_WINDOW'; id: string; x: number; y: number }
  | { type: 'RESIZE_WINDOW'; id: string; w: number; h: number }
  | { type: 'MINIMIZE'; id: string }
  | { type: 'MAXIMIZE'; id: string }
  | { type: 'RESTORE'; id: string }
  | { type: 'TOGGLE_START'; open?: boolean }
  | { type: 'SET_CAMERA'; scale: number; pan: { x: number; y: number } }
  | { type: 'AUDIO_UNLOCK' }
  | { type: 'TOGGLE_AMBIENT' }
  | { type: 'TOGGLE_MUSIC' }
  | { type: 'SET_MUTE'; mute: boolean }
  | { type: 'SET_AMBIENT'; on: boolean }
  | { type: 'SET_WALLPAPER'; id: string }
  | { type: 'SET_WALLPAPER_FIT'; fit: WallpaperFit }
  | { type: 'SET_DESKTOP_COLOR'; hex: string }
  | { type: 'SET_UI_SOUNDS'; on: boolean }
  | { type: 'SET_SKIP_BOOT'; on: boolean }
  | { type: 'BOOT_DONE' }
  | { type: 'QUEUE_HASH'; hash: string | null }
  | { type: 'EASTER_EGG'; eggId: string }
  | { type: 'SET_REDUCED'; value: boolean };

type Listener = (state: AppState, event: Event) => void;

const reduced =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let zCounter = 10;

export function nextZ(): number {
  zCounter += 1;
  return zCounter;
}

function loadMute(): boolean {
  return loadPref('desk.mute', '0') === '1';
}

function loadFit(): WallpaperFit {
  const raw = loadPref('desk.wallpaperFit', 'fill');
  return isFit(raw) ? raw : 'fill';
}

let state: AppState = {
  view: 'desktop',
  transition: 'none',
  windows: [],
  focusedId: null,
  startOpen: false,
  userScale: 1,
  userPan: { x: 0, y: 0 },
  audioUnlocked: false,
  musicOn: false,
  reducedMotion: reduced,
  bootSplash: false,
  queuedHash: null,
  lastEgg: null,
  mute: loadMute(),
  wallpaperId: loadPref('desk.wallpaper', 'supes'),
  wallpaperFit: loadFit(),
  desktopColor: loadPref('desk.desktopColor', '#008080'),
  uiSounds: loadPref('desk.uiSounds', '1') !== '0',
  skipBoot: loadPref('desk.skipBoot', '0') === '1',
  ambientOn: loadPref('desk.ambient', '0') === '1',
};

const listeners = new Set<Listener>();

export function getState(): AppState {
  return state;
}

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function mergePayload(
  current: Record<string, unknown> | undefined,
  incoming: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const next = { ...current, ...incoming };
  for (const [k, v] of Object.entries(next)) {
    if (v === '' || v === null || v === undefined) delete next[k];
  }
  return Object.keys(next).length ? next : undefined;
}

function reduce(s: AppState, e: Event): AppState {
  switch (e.type) {
    case 'ENTER_DESKTOP':
      if (s.view === 'desktop' && s.transition === 'none') return { ...s, startOpen: false };
      return {
        ...s,
        view: s.reducedMotion ? 'desktop' : s.view,
        transition: s.reducedMotion ? 'none' : 'zoom-in',
        startOpen: false,
      };
    case 'LEAVE_DESKTOP':
      if (s.view === 'room' && s.transition === 'none') return { ...s, startOpen: false };
      return {
        ...s,
        view: s.reducedMotion ? 'room' : s.view,
        transition: s.reducedMotion ? 'none' : 'zoom-out',
        startOpen: false,
        bootSplash: false,
      };
    case 'TRANSITION_END':
      return {
        ...s,
        view: s.transition === 'zoom-in' ? 'desktop' : s.transition === 'zoom-out' ? 'room' : s.view,
        transition: 'none',
      };
    case 'OPEN_WINDOW': {
      const existing = s.windows.find((w) => w.appId === e.appId);
      if (existing) {
        return {
          ...s,
          startOpen: false,
          focusedId: existing.id,
          windows: s.windows.map((w) =>
            w.id === existing.id
              ? {
                  ...w,
                  payload: mergePayload(w.payload, e.payload),
                  state: w.state === 'minimized' ? 'normal' : w.state,
                  z: nextZ(),
                }
              : w,
          ),
        };
      }
      return s;
    }
    case 'CLOSE_WINDOW': {
      const windows = s.windows.filter((w) => w.id !== e.id);
      const top = [...windows].sort((a, b) => b.z - a.z)[0];
      return { ...s, windows, focusedId: top?.id ?? null, startOpen: false };
    }
    case 'FOCUS_WINDOW':
      return {
        ...s,
        focusedId: e.id,
        startOpen: false,
        windows: s.windows.map((w) => (w.id === e.id ? { ...w, z: nextZ() } : w)),
      };
    case 'MOVE_WINDOW':
      return {
        ...s,
        windows: s.windows.map((w) => (w.id === e.id ? { ...w, x: e.x, y: e.y } : w)),
      };
    case 'RESIZE_WINDOW':
      return {
        ...s,
        windows: s.windows.map((w) => (w.id === e.id ? { ...w, w: e.w, h: e.h } : w)),
      };
    case 'MINIMIZE':
      return {
        ...s,
        windows: s.windows.map((w) => (w.id === e.id ? { ...w, state: 'minimized' } : w)),
      };
    case 'MAXIMIZE':
      return {
        ...s,
        windows: s.windows.map((w) =>
          w.id === e.id
            ? {
                ...w,
                state: 'maximized',
                restore: { x: w.x, y: w.y, w: w.w, h: w.h },
              }
            : w,
        ),
      };
    case 'RESTORE':
      return {
        ...s,
        windows: s.windows.map((w) =>
          w.id === e.id && w.restore
            ? { ...w, state: 'normal', x: w.restore.x, y: w.restore.y, w: w.restore.w, h: w.restore.h }
            : w.id === e.id
              ? { ...w, state: 'normal' }
              : w,
        ),
      };
    case 'TOGGLE_START':
      return { ...s, startOpen: e.open ?? !s.startOpen };
    case 'SET_CAMERA':
      return { ...s, userScale: e.scale, userPan: e.pan };
    case 'AUDIO_UNLOCK':
      return { ...s, audioUnlocked: true };
    case 'TOGGLE_AMBIENT': {
      const ambientOn = !s.ambientOn;
      savePref('desk.ambient', ambientOn ? '1' : '0');
      return { ...s, ambientOn, audioUnlocked: true };
    }
    case 'SET_AMBIENT':
      savePref('desk.ambient', e.on ? '1' : '0');
      return { ...s, ambientOn: e.on, audioUnlocked: true };
    case 'TOGGLE_MUSIC':
      return { ...s, musicOn: !s.musicOn, audioUnlocked: true };
    case 'SET_MUTE':
      savePref('desk.mute', e.mute ? '1' : '0');
      return { ...s, mute: e.mute };
    case 'SET_WALLPAPER':
      savePref('desk.wallpaper', e.id);
      return { ...s, wallpaperId: e.id };
    case 'SET_WALLPAPER_FIT':
      savePref('desk.wallpaperFit', e.fit);
      return { ...s, wallpaperFit: e.fit };
    case 'SET_DESKTOP_COLOR':
      savePref('desk.desktopColor', e.hex);
      return { ...s, desktopColor: e.hex };
    case 'SET_UI_SOUNDS':
      savePref('desk.uiSounds', e.on ? '1' : '0');
      return { ...s, uiSounds: e.on };
    case 'SET_SKIP_BOOT':
      savePref('desk.skipBoot', e.on ? '1' : '0');
      return { ...s, skipBoot: e.on };
    case 'BOOT_DONE':
      return { ...s, bootSplash: false };
    case 'QUEUE_HASH':
      return { ...s, queuedHash: e.hash };
    case 'EASTER_EGG':
      return { ...s, lastEgg: e.eggId };
    case 'SET_REDUCED':
      return { ...s, reducedMotion: e.value };
    default:
      return s;
  }
}

export function dispatch(event: Event): AppState {
  const next = reduce(state, event);
  state = next;
  for (const fn of listeners) fn(state, event);
  return state;
}

export function addWindow(win: WindowInstance): void {
  state = {
    ...state,
    windows: [...state.windows, win],
    focusedId: win.id,
    startOpen: false,
  };
  for (const fn of listeners) fn(state, { type: 'OPEN_WINDOW', appId: win.appId, payload: win.payload });
}
